import type { DateTime } from "luxon";
import type { TranslationKeys } from "../../i18n/types";
import { NurseTrainingStatus } from "../../utils/models";
import { formatCurrency } from "../../utils/utilFunctions";
import type { TrainingPayStatus } from "../team/WageSettings/deepLink";

/*
 * The wage breakdown panel's shared constants and formatters — one definition each, so every file of the
 * panel hides amounts, rounds cents and writes times the same way.
 */

/** Marks a rule from another department; the panel's footnote says what it means. */
export const OTHER_DEPARTMENT_MARKER = "*";

/** What an amount reads as while wages are hidden (the reveal toggle off). */
const HIDDEN_AMOUNT = "-";

export const CENTS_PER_DOLLAR = 100;
export const MINUTES_PER_HOUR = 60;
/** Hours in running text keep two decimals at most: 40.75, 12. */
const HOURS_DISPLAY_FACTOR = 100;

/** A trainee's or orientee's pay is titled like the Wage Settings card that sets it. */
export const TRAINING_PAY_LINK_LABEL: Readonly<Record<TrainingPayStatus, TranslationKeys>> = {
	[NurseTrainingStatus.Trainee]: "team.wageSettings.training.label",
	[NurseTrainingStatus.Orientation]: "team.wageSettings.orientation.label",
};

export const formatCents = (cents: number): string => formatCurrency(Math.abs(cents) / CENTS_PER_DOLLAR);

/** "+$2.50", "−$1.00" — a true minus sign, so the column's figures keep one width. */
const formatSignedCents = (cents: number): string => `${cents < 0 ? "−" : "+"}${formatCents(cents)}`;

/** An amount, or the hidden marker while wages are hidden. Labels and links never go through this. */
export const moneyOrHidden = (cents: number, canViewWage: boolean): string => (canViewWage ? formatCents(cents) : HIDDEN_AMOUNT);

export const signedMoneyOrHidden = (cents: number, canViewWage: boolean): string => (canViewWage ? formatSignedCents(cents) : HIDDEN_AMOUNT);

/** Hours as people write them: 12, 40.75 — no trailing zeros. */
export const formatHoursNumber = (hours: number): string => String(Math.round(hours * HOURS_DISPLAY_FACTOR) / HOURS_DISPLAY_FACTOR);

/** Minutes as a clock duration: 30 → "0:30", 750 → "12:30". */
export const formatClockDuration = (minutes: number): string => {
	const rounded = Math.round(minutes);
	return `${Math.floor(rounded / MINUTES_PER_HOUR)}:${String(rounded % MINUTES_PER_HOUR).padStart(2, "0")}`;
};

const NO_BREAK_SPACE = " ";
const TIME_FORMAT = "h:mm a";
const SHIFT_DATE_FORMAT = "ccc d LLL";

/** "2:30 PM", bound so it never wraps apart. */
export const formatClockTime = (time: DateTime): string => time.toFormat(TIME_FORMAT).replaceAll(" ", NO_BREAK_SPACE);

/** "Sun 30 Aug" — the day a shift starts on, bound like a time range so it never wraps apart. */
export const formatShiftDate = (start: DateTime): string => start.toFormat(SHIFT_DATE_FORMAT).replaceAll(" ", NO_BREAK_SPACE);

/**
 * "6:00 PM – 6:30 PM", bound with non-breaking spaces so a wrapping line breaks at its " · " separators and
 * never inside a range.
 */
export const formatTimeRange = (start: DateTime, end: DateTime): string =>
	[start, end].map((time) => time.toFormat(TIME_FORMAT).replaceAll(" ", NO_BREAK_SPACE)).join(`${NO_BREAK_SPACE}–${NO_BREAK_SPACE}`);
